// Write (the Mac's WritePage): the selected scene, a folder's scenes or the whole book in one scrivenings editor (with
// the contents' filter on, only the documents it lets through, under a banner that says so), with
// chapter openers, the format bar and the footer (the words shown, today's count, a writing session, the nearest word target, the
// total). The same view reads: where the project can't be written (not open live yet, the Trash) the editor is
// read-only.
//
// The margin beside the text lists the shown scenes' open comments and suggestions in reading order; the text washes
// their passages. Add Comment (⌥⌘A, the format bar's Comment, or the text's context menu with a selection) comments
// on the selection within one scene. The caret in a passage picks its card; a card selects its passage.
//
// Composition mode (⌥⌘F, or Compose): the page fills the window on its slate, the chrome fades while typing and comes
// back when the pointer moves; Escape leaves it. It's a modal: focus stays inside it. The caret is remembered per
// project (Left off) in this browser.
import { MapDocumentPage } from './maps/MindMapsPage.tsx';
import { storageId } from '../store/storageId.ts';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { StateCommand } from '@codemirror/state';
import { scalarOffsetOfUTF16, StoreEditor, type DocumentEditor, type OpenedProject } from '@writing-suite/core';
import { goalMet, progress, remaining, sprintClock, sprintNet } from '@writing-suite/core';
import { commands, linkAtPosition, rules, type ProseFormat } from '@writing-suite/editor';
import type { PageProps } from '../shell/ProjectShell.tsx';
import { announce, usePreference } from '../shell/context.ts';
import { useWorkspace } from '../shell/workspace.ts';
import type { LiveProject } from '../store/project.ts';
import { sessionDisplay, type Writing, type WritingState } from '../store/writing.ts';
import { annotationsOf, fragmentEntries, isDetached, type MarginEntry } from './comments.ts';
import { FormatBar, LINE_SPACINGS } from './FormatBar.tsx';
import { formatNumber, formatWords, readingFragments } from './helpers.ts';
import { MarginCard, TextDialog } from './Margin.tsx';
import { CommentCategoriesDialog } from './CommentCategories.tsx';
import { commentCategoriesOf, setCommentCategory } from '../store/revise.ts';
import { locksOf } from '../store/planModel.ts';
import { MenuButton, MenuPopup, type MenuEntry } from './Menu.tsx';
import { Scrivenings, type ScriveningsApi } from './Scrivenings.tsx';
import { nearestTarget, postPlace } from './structure.ts';
import { NothingOpen } from './Home.tsx';
import { FilterBanner } from './FilterBar.tsx';
import { leftOffUTF16, loadLeftOff, saveLeftOff } from '../store/leftOff.ts';
import { newPostCaret, placedNewPostCaret } from '../store/newPostCaret.ts';
import { RevisionCard, RevisionMenu, RevisionSheets, useRevisionMode, type RevisionMode, type RevisionSheet } from './revise/Revisions.tsx';
import { LinkCard, LinkDialog, linkableDocuments, linkedDocumentName, openLink } from './Links.tsx';

/**
 * More items for the text's context menu (a room's own: Add to Glossary…), each given the selection (UTF-16 in its
 * fragment, and its text) and the project. Register at module load; returns a function that unregisters.
 */
export interface TextMenuContext {
  project: PageProps['project'];
  live: LiveProject | null;
  selection: { fragmentId: string; from: number; to: number; text: string } | null;
  canWrite: boolean;
}
export type TextMenuProvider = (ctx: TextMenuContext) => MenuEntry[];
const textMenuProviders = new Set<TextMenuProvider>();
export function registerTextMenuItems(provider: TextMenuProvider): () => void {
  textMenuProviders.add(provider);
  return () => textMenuProviders.delete(provider);
}
import { recordOpened } from '../store/plan.ts';
import { navigate } from '../shell/route.ts';
import { AlternateBanner, alternateFragments } from './plan/Alternates.tsx';
import { PresenceClaim, PresenceLine } from './Presence.tsx';
import { isPicture, useAttachmentURL } from './Attachments.tsx';
import type { AttachmentRef } from '@writing-suite/core';
// Live co-writing in the editor (W9b): other writers' carets, this writer's presence, markers kept balanced.
import '../store/cowriting.ts';

interface Caret {
  fragmentId: string;
  offset: number;
}

const EMPTY: WritingState = { session: null, sprint: null, finished: null, log: [] };

/** A writing store's state, following it. */
export function useWritingState(writing: Writing | null): WritingState {
  return useSyncExternalStore(
    (fn) => writing?.subscribe(fn) ?? (() => {}),
    () => writing?.state ?? EMPTY,
  );
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), .scrivenings';

/** Write and Research: a mind map chosen in the contents shows its canvas in place of the editor. */
export function WritePage(props: PageProps) {
  const fragment = props.node && !props.node.isFolder && props.node.fragmentId ? props.project.fragment(props.node.fragmentId) : null;
  if (fragment?.kind === 'map') return <MapDocumentPage {...props} fragmentId={fragment.id} />;
  return <TextPage {...props} />;
}

function TextPage({ projectId, project, tree, node, arrangement, live, params, hrefFor }: PageProps) {
  const ws = useWorkspace();
  // An alternate scene open in its place (Plan's alternates, `?alternate=`).
  const alternate = alternateFragments(project, arrangement.id, node, params)?.[0] ?? null;
  const ids = useMemo(() => (alternate ? [alternate] : node ? readingFragments([node]) : readingFragments(tree)), [node, tree, alternate]);
  const idsKey = ids.join('\n');
  const openers = useMemo(() => project.openers(), [project]);
  const inManuscript = arrangement.id === project.manuscriptId();
  const heading = node ? node.title || 'Untitled' : inManuscript ? project.title : arrangement.name;
  const writable = !!ws?.canWrite;
  // A commenter (W9a) reads the text and adds comments and suggestions; everyone else who can write can too.
  const commentable = !!ws?.canComment;
  const commenter = ws?.role === 'commenter';
  const api = useRef<ScriveningsApi>(null);
  const [format, setFormatState] = useState<ProseFormat | null>(null);
  // The words in the editor's selection, for the footer (null with nothing selected).
  const [selectedWords, setSelectedWords] = useState<number | null>(null);
  // The format at the caret is reported on every keystroke: the page redraws only when it's different.
  const lastFormat = useRef('null');
  const setFormat = useCallback((f: ProseFormat | null) => {
    const key = JSON.stringify(f);
    if (key === lastFormat.current) return;
    lastFormat.current = key;
    setFormatState(f);
  }, []);
  const [composing, setComposing] = useState(false);
  const [typing, setTyping] = useState(false);
  const [lineSpacing, setLineSpacing] = usePreference(`lineSpacing-${storageId(project.id)}`, 'book');
  const [marginPref, setMarginPref] = usePreference('margin', true);
  const [active, setActive] = useState<string | null>(null);
  const [composer, setComposer] = useState<{ fragmentId: string; from: number; to: number; quote: string } | null>(null);
  const [suggesting, setSuggesting] = useState<{ fragmentId: string; from: number; to: number; quote: string } | null>(null);
  /** The category a comment being added goes in ("" for none), and the sheet that names the categories. */
  const [composerCategory, setComposerCategory] = useState('');
  const [editingCategories, setEditingCategories] = useState(false);
  /** The text's menu, where it was asked for, and the link there (or at the caret). */
  const [menu, setMenu] = useState<{ x: number; y: number; link: string | null } | null>(null);
  /** Add Link… or Edit Link… open: the address of the link being edited (null when adding one). */
  const [linking, setLinking] = useState<{ existing: string | null } | null>(null);
  const page = useRef<HTMLDivElement>(null);

  useEffect(() => ws?.setShownFragments(ids), [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Revision mode: the marks, the card at the caret, the footer's menu and its sheets.
  const revision = useRevisionMode({ project, live, ids, api });
  const revisionRef = useRef(revision);
  revisionRef.current = revision;
  const [revisionSheet, setRevisionSheet] = useState<RevisionSheet | null>(null);
  // Opened in this browser (the corkboard's cold scenes).
  useEffect(() => recordOpened(project.id, ids), [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Left off: where the caret was in this project, restored when the selection holds it (stored in scalars, placed in
  // UTF-16).
  const startAt = useMemo(() => {
    const textOf = (id: string) => live?.text(id) ?? project.fragment(id)?.text ?? null;
    const leftOff = loadLeftOff(projectId, project, textOf);
    if (!leftOff || !ids.includes(leftOff.fragmentId)) return null;
    return { fragmentId: leftOff.fragmentId, offset: leftOffUTF16(leftOff, textOf(leftOff.fragmentId) ?? '') };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, idsKey]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const liveRef = useRef(live);
  liveRef.current = live;
  const projectRef = useRef(project);
  projectRef.current = project;
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;
  const pendingLeftOff = useRef<(() => void) | null>(null);
  const caretFragment = useRef<string | null | undefined>(undefined);
  const onCaret = useCallback(
    (c: Caret | null) => {
      revisionRef.current.onCaret(c);
      // The inspector follows the caret's scene: told only when it moves to another.
      if (caretFragment.current !== (c?.fragmentId ?? null)) {
        caretFragment.current = c?.fragmentId ?? null;
        ws?.setCaretFragment(caretFragment.current);
      }
      if (!c) return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      pendingLeftOff.current = () => {
        pendingLeftOff.current = null;
        const text = liveRef.current?.text(c.fragmentId) ?? projectRef.current.fragment(c.fragmentId)?.text ?? '';
        saveLeftOff(projectIdRef.current, c.fragmentId, c.offset, text);
      };
      saveTimer.current = setTimeout(() => pendingLeftOff.current?.(), 300);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id, ws?.setCaretFragment],
  );
  // Leaving the page keeps the last caret (the Library's Home reads it at once).
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      pendingLeftOff.current?.();
    },
    [],
  );

  // The page's own focus for the editor (after naming a new scene, closing a menu).
  const focus = useCallback(() => api.current?.handle()?.focus(), []);
  useEffect(() => {
    ws?.setEditorFocus(focus);
    return () => ws?.setEditorFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  // The shown scenes' comments and suggestions, resolved in the text as it is now (the editor's).
  const entries = useMemo(() => {
    const opened = project as OpenedProject;
    if (typeof opened.fragmentRecord !== 'function') return [];
    return ids.flatMap((id) => fragmentEntries(opened, id, live?.doc(`fragment.${id}`) ?? opened.fragmentDoc(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, idsKey, live]);
  // A comment in a category is underlined in the category's colour.
  const categories = useMemo(() => commentCategoriesOf(project), [project]);
  // Documents under lock: the editor refuses typing in them, and the head says so.
  const locks = useMemo(() => locksOf(project), [project]);
  const lockedHere = ids.filter((id) => locks.has(id));
  const annotations = useMemo(() => annotationsOf(entries, categories), [entries, categories]);
  const editCategories = writable ? () => setEditingCategories(true) : undefined;
  const found = entries.filter((e) => !isDetached(e));
  const detached = entries.filter(isDetached);
  const showsMargin = marginPref && !composing && entries.length > 0;

  // A passage asked for (Revise, Find): selected once the editor shows its scene.
  useEffect(() => {
    const target = ws?.revealing;
    if (!target || !ids.includes(target.fragmentId)) return;
    if (api.current?.reveal(target.fragmentId, target.from ?? 0, target.to ?? target.from ?? 0)) ws?.clearReveal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws?.revealing, idsKey]);

  // A new journal post: the caret on the empty line between its opening and sign-off, the keyboard left where it is (the
  // title being renamed; Enter there brings it to the text). The editor may take a frame or two to show the post.
  useEffect(() => {
    const waiting = newPostCaret(ids);
    if (!waiting) return;
    let tries = 0, frame = 0;
    const place = () => {
      if (api.current?.reveal(waiting.fragmentId, waiting.offset, waiting.offset, waiting.focus)) placedNewPostCaret(waiting.fragmentId);
      else if (++tries < 30) frame = requestAnimationFrame(place);
    };
    place();
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const run = (command: StateCommand) => {
    const h = api.current?.handle();
    if (!h) return;
    h.run(command);
    h.focus();
    setFormat(h.format());
  };

  const choose = (e: MarginEntry) => {
    setActive(e.id);
    const now = api.current?.annotationRange(e.id);
    const range = now ? [now.from, now.to] : e.range;
    if (range) api.current?.reveal(e.fragmentId, range[0], range[1]);
  };

  /** Add Comment: the selection, within one scene and not empty. */
  const addComment = useCallback(() => {
    if (!commentable) return;
    const sel = api.current?.handle()?.selection();
    if (!sel || sel.to <= sel.from) {
      announce('Select some text in one scene to comment on it.');
      return;
    }
    const text = live?.text(sel.fragmentId) ?? '';
    setComposerCategory('');
    setComposer({ ...sel, quote: text.slice(sel.from, sel.to) });
  }, [commentable, live]);

  /** Suggest Change: the selection (within one scene) and the text to put in its place, as a suggestion the project's
   *  writers accept or reject — the text itself doesn't change (as agents' `propose_suggestion`). */
  const suggestChange = useCallback(() => {
    if (!commentable) return;
    const sel = api.current?.handle()?.selection();
    if (!sel || sel.to <= sel.from) {
      announce('Select some text in one scene to suggest a change to it.');
      return;
    }
    const text = live?.text(sel.fragmentId) ?? '';
    setSuggesting({ ...sel, quote: text.slice(sel.from, sel.to) });
  }, [commentable, live]);

  // Links: Add Link… / Edit Link… for the selection (or the link the caret is in, or the word there); a link opens in a new
  // tab, or a document of this project in the app.
  const editLink = useCallback(() => {
    if (!writable) return;
    const h = api.current?.handle();
    if (!h || !h.format()) return;
    setLinking({ existing: h.linkAtSelection()?.address ?? null });
  }, [writable]);
  const saveLink = (typed: string): string | null => {
    const h = api.current?.handle();
    if (!h) return null;
    if (h.run(commands.setLink(typed))) {
      setLinking(null);
      h.focus();
      setFormat(h.format());
      return null;
    }
    return rules.linkAddressFromTyped(typed) === null ? 'That isn’t a web or email address.' : 'Select the words to link first.';
  };
  const removeLink = () => {
    setLinking(null);
    run(commands.removeLink);
  };
  const followLink = useCallback(
    (address: string) => openLink(address, (fragmentId) => {
      if (projectRef.current.fragment(fragmentId)) ws?.reveal({ fragmentId, from: 0, to: 0 });
      else announce('That document isn’t in this project.');
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ws?.reveal],
  );

  // Typing fades the composition chrome; the pointer brings it back.
  const onTyped = useCallback(() => {
    setTyping(true);
    revisionRef.current.onTyped();
  }, []);
  const wake = () => {
    if (typing) setTyping(false);
  };

  const compose = useCallback((on: boolean) => {
    setComposing(on);
    setTyping(false);
    requestAnimationFrame(() => api.current?.handle()?.focus());
  }, []);
  // ⌥⌘F (Ctrl+Alt+F elsewhere) enters and leaves; Escape leaves; ⌥⌘A adds a comment.
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.altKey && e.code === 'KeyF') {
        e.preventDefault();
        compose(!composing);
      } else if (mod && e.altKey && e.code === 'KeyA') {
        e.preventDefault();
        addComment();
      } else if (mod && e.altKey && e.code === 'KeyL') {
        e.preventDefault();
        editLink();
      } else if (e.key === 'Escape' && composing && !e.defaultPrevented) {
        e.preventDefault();
        compose(false);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [composing, compose, addComment, editLink]);

  // Composition is a modal: Tab and Shift-Tab go round what's in it, and focus that lands outside comes back.
  useEffect(() => {
    if (!composing) return;
    const inside = (el: EventTarget | null) => !!el && !!page.current && page.current.contains(el as Node);
    const focusables = () => [...(page.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])].filter((el) => el.offsetParent !== null || el === document.activeElement);
    const onFocus = (e: FocusEvent) => {
      if (inside(e.target) || (e.target as Element | null)?.closest?.('.menu, dialog, .find-panel')) return;
      api.current?.handle()?.focus();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.defaultPrevented) return;
      const all = focusables();
      if (!all.length) return;
      const current = document.activeElement as HTMLElement | null;
      const i = all.findIndex((el) => el === current || el.contains(current));
      if (e.shiftKey && i <= 0) {
        e.preventDefault();
        all[all.length - 1].focus();
      } else if (!e.shiftKey && (i === -1 || i === all.length - 1)) {
        e.preventDefault();
        if (all[0].classList.contains('scrivenings')) api.current?.handle()?.focus();
        else all[0].focus();
      }
    };
    document.addEventListener('focusin', onFocus);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('keydown', onKey);
    };
  }, [composing]);

  const spacing = LINE_SPACINGS.find((s) => s.id === lineSpacing) ?? LINE_SPACINGS[2];
  useEffect(() => page.current?.style.setProperty('--write-line-height', String(spacing.value)), [spacing.value]);

  const composeButton = (
    <button type="button" className="quiet-button compose-button" onMouseDown={(e) => e.preventDefault()} onClick={() => compose(!composing)} aria-pressed={composing} title="Composition mode (⌥⌘F)">
      {composing ? 'Done' : 'Compose'}
    </button>
  );
  const caretLink = format?.link ?? null;
  const contextEntries = (menuLink: string | null): MenuEntry[] => [
    ...(menuLink
      ? [
          { label: 'Open Link', onSelect: () => followLink(menuLink) },
          ...(writable ? [{ label: 'Edit Link…', shortcut: '⌥⌘L', onSelect: editLink }, { label: 'Remove Link', onSelect: () => run(commands.removeLink) }] : []),
          { kind: 'separator' as const },
        ]
      : writable
        ? [{ label: 'Add Link…', shortcut: '⌥⌘L', onSelect: editLink }]
        : []),
    ...(writable ? extraTextEntries() : []),
    { label: 'Add Comment…', shortcut: '⌥⌘A', onSelect: addComment },
    { label: 'Suggest Change…', onSelect: suggestChange },
    ...(writable ? [{ label: 'Comment Categories…', onSelect: () => setEditingCategories(true) }] : []),
    {
      label: 'Copy',
      shortcut: '⌘C',
      onSelect: () => {
        const copied = api.current?.handle()?.copySelection();
        if (copied) void navigator.clipboard?.writeText(copied.plain).catch(() => {});
        focus();
      },
    },
  ];

  /** The rooms' own context-menu items for the selection. */
  function extraTextEntries(): MenuEntry[] {
    if (!textMenuProviders.size) return [];
    const sel = api.current?.handle()?.selection() ?? null;
    const text = sel ? (api.current?.text(sel.fragmentId) ?? '').slice(sel.from, sel.to) : '';
    const ctx: TextMenuContext = { project, live, selection: sel ? { ...sel, text } : null, canWrite: writable };
    const items = [...textMenuProviders].flatMap((p) => p(ctx));
    return items.length ? [...items, { kind: 'separator' }] : [];
  }

  return (
    <div
      ref={page}
      className={`write-page${composing ? ' composing' : ''}${typing ? ' typing' : ''}${showsMargin ? ' with-margin' : ''}`}
      data-composing={composing || undefined}
      onPointerMove={wake}
      role={composing ? 'dialog' : undefined}
      aria-modal={composing || undefined}
      aria-label={composing ? `Composing ${heading}` : undefined}
    >
      <header className="write-head">
        <h1 id="write-heading" tabIndex={-1}>
          {heading}
        </h1>
        {writable && lockedHere.length ? (
          <p className="read-only-note" data-read-only="locked">
            {lockedHere.length === ids.length ? 'Locked — unlock it on the shelf to change it' : 'Some of this is locked'}
          </p>
        ) : null}
        {!writable ? (
          <p className="read-only-note" data-read-only={commenter ? 'commenter' : 'true'}>
            {arrangement.role === 'trash'
              ? 'In the Trash — read only'
              : commenter && commentable
                ? 'You can comment and suggest changes'
                : 'Read only'}
          </p>
        ) : null}
        <div className="format-row">
          <FormatBar format={format} run={run} disabled={!writable} lineSpacing={spacing.id} setLineSpacing={setLineSpacing} onLink={editLink}>
            <button type="button" className="quiet-button comment-button" onMouseDown={(e) => e.preventDefault()} onClick={addComment} disabled={!commentable} title="Add Comment (⌥⌘A)">
              Comment
            </button>
            {commenter ? (
              <button type="button" className="quiet-button suggest-button" onMouseDown={(e) => e.preventDefault()} onClick={suggestChange} disabled={!commentable} title="Suggest a change to the selection">
                Suggest
              </button>
            ) : null}
            {entries.length ? (
              <button
                type="button"
                className="quiet-button margin-toggle"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setMarginPref(!marginPref)}
                aria-pressed={marginPref}
                aria-controls={showsMargin ? 'margin' : undefined}
                title={marginPref ? 'Hide the margin' : 'Show the margin'}
              >
                {entries.length === 1 ? '1 note' : `${entries.length} notes`}
              </button>
            ) : null}
            {composeButton}
          </FormatBar>
        </div>
      </header>
      <AlternateBanner project={project} arrangement={arrangement} node={node} params={params} live={live} hrefFor={hrefFor} navigate={navigate} />
      <FilterBanner />
      <PresenceClaim />
      {arrangement.role === 'reference' ? <PicturesStrip project={project} live={live} ids={ids} /> : null}
      <div className="write-body">
        {ids.length === 0 ? (
          <NothingOpen projectId={projectId} project={project} live={live} note="Nothing here yet." />
        ) : (
          <Scrivenings
            apiRef={api}
            project={project}
            live={live}
            ids={ids}
            openers={openers}
            readOnly={!writable}
            locked={lockedHere}
            composition={composing}
            startAt={startAt}
            label={`Text of ${heading}`}
            annotations={annotations}
            activeAnnotation={active}
            onCaret={onCaret}
            onFormat={setFormat}
            onSelection={setSelectedWords}
            onTyped={onTyped}
            onAnnotationCaret={(found) => found.length && setActive(found[found.length - 1])}
            onContextMenu={(e) => {
              const h = api.current?.handle();
              const sel = h?.selection();
              // A link under the pointer: with nothing selected the caret goes into it (as a Mac's text view puts it),
              // so Edit and Remove act on that link.
              const pos = h ? h.view.posAtCoords({ x: e.clientX, y: e.clientY }) : null;
              const clicked = h && pos !== null ? linkAtPosition(h.view.state, pos) : null;
              if (h && clicked && h.view.state.selection.main.empty) h.view.dispatch({ selection: { anchor: pos! } });
              const link = clicked?.address ?? h?.linkAtSelection()?.address ?? null;
              if (!link && (!commentable || !sel || sel.to <= sel.from)) return; // the browser's own menu
              e.preventDefault();
              setMenu({ x: e.clientX, y: e.clientY, link });
            }}
            onOpenLink={followLink}
          />
        )}
        {showsMargin ? (
          <aside className="margin" id="margin" aria-label="Comments and suggestions">
            <h2 className="eyebrow">In the margin</h2>
            {found.map((e) => (
              <MarginCard
                key={e.id}
                entry={e}
                active={e.id === active}
                actions={{ live, canWrite: writable, canComment: commentable, replaceInEditor: (f, a, b, t) => !!api.current?.replace(f, a, b, t) }}
                onChoose={() => choose(e)}
                categories={categories}
                onEditCategories={editCategories}
              />
            ))}
            {detached.length ? (
              <section className="detached-list" aria-labelledby="detached-title">
                <h3 id="detached-title" className="eyebrow">
                  Detached
                </h3>
                {detached.map((e) => (
                  <MarginCard key={e.id} entry={e} active={false} actions={{ live, canWrite: writable, canComment: commentable }} categories={categories} onEditCategories={editCategories} />
                ))}
              </section>
            ) : null}
          </aside>
        ) : null}
      </div>
      {revision.caretChange && !composing ? <RevisionCard mode={revision} canWrite={writable} /> : null}
      {caretLink && !revision.caretChange && !composing ? (
        <LinkCard
          address={caretLink}
          documentTitle={linkedDocumentName(project, caretLink)}
          canWrite={writable}
          onOpenDocument={(fragmentId) => followLink(`fragment:${fragmentId}`)}
          onEdit={editLink}
          onRemove={() => run(commands.removeLink)}
        />
      ) : null}
      <WriteFooter
        project={project}
        live={live}
        tree={tree}
        ids={ids}
        selectedWords={selectedWords}
        node={node}
        composing={composing}
        onExit={() => compose(false)}
        revision={revision}
        canWrite={writable}
        openRevisionSheet={setRevisionSheet}
      />
      <RevisionSheets sheet={revisionSheet} project={project} live={live} onClose={() => setRevisionSheet(null)} />
      {editingCategories && live ? <CommentCategoriesDialog project={project} live={live} onClose={() => setEditingCategories(false)} /> : null}
      {composer ? (
        <TextDialog
          title="Add Comment"
          label="Comment"
          confirm="Add"
          multiline
          message={<p className="comment-quote">“{composer.quote.length > 160 ? `${composer.quote.slice(0, 159)}…` : composer.quote}”</p>}
          extra={
            categories.length ? (
              <label className="field-label stacked-label comment-category-field">
                Category
                <select value={composerCategory} onChange={(e) => setComposerCategory(e.target.value)}>
                  <option value="">No category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null
          }
          onCancel={() => {
            setComposer(null);
            focus();
          }}
          onDone={(body) => {
            const c = composer;
            setComposer(null);
            const made = live?.edit((e) => e.addComment(c.fragmentId, c.from, c.to, body));
            if (made && live && composerCategory) setCommentCategory(live, c.fragmentId, made.id, composerCategory);
            if (made) setActive(made.id);
            announce('Comment added');
            focus();
          }}
        />
      ) : null}
      {suggesting ? (
        <TextDialog
          title="Suggest Change"
          label="Replace with"
          initial={suggesting.quote}
          confirm="Suggest"
          multiline
          message={<p className="comment-quote"><del>{suggesting.quote.length > 160 ? `${suggesting.quote.slice(0, 159)}…` : suggesting.quote}</del></p>}
          onCancel={() => {
            setSuggesting(null);
            focus();
          }}
          onDone={(proposed) => {
            const c = suggesting;
            setSuggesting(null);
            const made = live ? suggest(live, c, proposed) : null;
            if (made) setActive(made);
            announce('Suggestion added');
            focus();
          }}
        />
      ) : null}
      {menu ? <MenuPopup entries={contextEntries(menu.link)} at={menu} label="Text" onClose={() => setMenu(null)} /> : null}
      {linking ? (
        <LinkDialog
          existing={linking.existing}
          documents={linkableDocuments(project)}
          onSave={saveLink}
          onRemove={removeLink}
          onCancel={() => {
            setLinking(null);
            focus();
          }}
        />
      ) : null}
    </div>
  );
}

/** A suggestion on a passage (UTF-16 in its fragment), through the core's suggestion writes; returns its id. */
function suggest(live: LiveProject, at: { fragmentId: string; from: number; to: number }, proposed: string): string | null {
  return live.edit((e) => {
    const d = e as unknown as DocumentEditor;
    const text = live.text(at.fragmentId);
    const range: [number, number] = [scalarOffsetOfUTF16(text, at.from), scalarOffsetOfUTF16(text, at.to)];
    return new StoreEditor(d.docs, d.context).addSuggestion(at.fragmentId, { range, proposedText: proposed, author: d.context.author, authorKind: 'human', message: 'Suggestion' }).id;
  });
}

/** The session's figures: net since it began, and written and cut including typing not yet saved (`sessionTally`). */
function sessionFigures(state: WritingState, total: number, unsaved: { added: number; removed: number }) {
  const s = state.session;
  if (!s) return null;
  return { net: total - s.baseline, added: s.added + unsaved.added, removed: s.removed + unsaved.removed };
}

function WriteFooter({
  project,
  live,
  tree,
  ids,
  selectedWords,
  node,
  composing,
  onExit,
  revision,
  canWrite,
  openRevisionSheet,
}: {
  project: PageProps['project'];
  live: LiveProject | null;
  tree: PageProps['tree'];
  ids: string[];
  /** The words in the editor's selection, shown in the open text's count's place while there are any. */
  selectedWords: number | null;
  node: PageProps['node'];
  composing: boolean;
  onExit: () => void;
  revision: RevisionMode;
  canWrite: boolean;
  openRevisionSheet(sheet: RevisionSheet): void;
}) {
  const ws = useWorkspace();
  const writing = ws?.writing ?? null;
  const state = useWritingState(writing);
  const total = project.manuscriptWords();
  const shown = ids.reduce((n, id) => n + project.words(id), 0);
  const counted = useMemo(() => new Set(project.counted(project.targets().countAllText)), [project]);
  const unsaved = live ? live.unsavedChange(counted, (id) => project.words(id)) : { added: 0, removed: 0 };
  useEffect(() => void writing?.ensureSession(), [writing, project]);
  const session = sessionFigures(state, total, unsaved);
  const targets = project.targets();
  // `chapterTarget`: the nearest word target at or above the selected item (the page's), against its subtree's words.
  const near = nearestTarget(tree, node?.id ?? null, (id) => project.fragment(id)?.metadata);
  const nearWords = near ? project.subtreeWords(near.node) : 0;
  // In a journal post with sections: which post, and which section of it.
  const manuscriptId = project.type === 'journal' ? project.manuscriptId() : null;
  const place = manuscriptId ? postPlace(project.tree(manuscriptId), ws?.caretFragment ?? null, (id) => project.fragment(id)?.metadata) : null;
  return (
    <footer className="write-foot" aria-label="Words">
      {selectedWords !== null ? (
        <span className="foot-selected" data-selected-words={selectedWords} title="Words in the selection">
          {formatWords(selectedWords)} selected
        </span>
      ) : (
        <span data-selection-words={shown}>{formatWords(shown)}</span>
      )}
      {place ? <span className="foot-post" data-post-place>{place}</span> : null}
      <PresenceLine ids={ids} titleOf={(id) => project.fragment(id)?.title ?? ''} />
      {ws?.filter?.view ? (
        // The contents are filtered: what's open may be only part of a folder.
        <button
          type="button"
          className="foot-button foot-filtered"
          onClick={() => ws.showColumn?.()}
          title="The shelf is filtered; Next and Previous Document step through what matches"
        >
          Filtered
        </button>
      ) : null}
      {session ? (
        <MenuButton
          label={`Today ${session.net >= 0 ? 'plus' : 'minus'} ${formatNumber(Math.abs(session.net))}${targets.session ? ` of ${formatNumber(targets.session)}` : ''}: ${formatNumber(session.added)} written, ${formatNumber(session.removed)} cut`}
          className="foot-button foot-session"
          title={`Today: ${formatNumber(session.added)} written, ${formatNumber(session.removed)} cut`}
          entries={() => [
            { kind: 'heading', label: `${formatNumber(session.added)} written, ${formatNumber(session.removed)} cut` },
            { label: 'Count From Now', onSelect: () => writing?.newSession() },
          ]}
        >
          <span data-session-words={session.net} data-session-added={session.added} data-session-removed={session.removed}>
            Today {session.net >= 0 ? '+' : '−'}
            {formatNumber(Math.abs(session.net))}
            {targets.session ? ` of ${formatNumber(targets.session)}` : ''}
          </span>
        </MenuButton>
      ) : null}
      <SprintStatus writing={writing} state={state} unsaved={unsaved} />
      <RevisionMenu mode={revision} canWrite={canWrite} open={openRevisionSheet} />
      {near ? (
        <span className="foot-target" data-target-words={nearWords} data-target={near.target}>
          <span className="meter" role="img" aria-label={`${Math.min(100, Math.round((100 * nearWords) / near.target))}% of ${near.node.title || 'Untitled'}’s target`}>
            <span className="meter-fill" ref={(el) => el?.style.setProperty('--fraction', String(Math.min(1, nearWords / near.target)))} />
          </span>
          {near.node.title || 'Untitled'}: {formatNumber(nearWords)} of {formatNumber(near.target)}
        </span>
      ) : null}
      <span className="foot-total" data-total-words={total}>
        {targets.manuscript ? `${formatNumber(total)} of ${formatWords(targets.manuscript)}` : `${formatWords(total)} in all`}
      </span>
      {composing ? (
        <button type="button" className="quiet-button foot-exit" onClick={onExit}>
          Leave composition
        </button>
      ) : null}
    </footer>
  );
}

/** The footer's writing session: time left and words, each only if the writer chose to see it (`sessionDisplay`; with neither,
 *  "Session" alone says one is running), the menu finishing or discarding it; or a button to start one. */
function SprintStatus({ writing, state, unsaved }: { writing: Writing | null; state: WritingState; unsaved: { added: number; removed: number } }) {
  const ws = useWorkspace();
  const [now, setNow] = useState(() => Date.now());
  const sprint = state.sprint && state.sprint.projectID === writing?.projectId ? state.sprint : null;
  useEffect(() => {
    if (!sprint) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [sprint?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!writing) return null;
  if (!sprint) {
    return (
      <button type="button" className="foot-button sprint-start" onClick={() => ws?.openSheet('sprint')} disabled={!ws?.canWrite} title="Start a writing session">
        Session
      </button>
    );
  }
  const live = { ...sprint, added: sprint.added + unsaved.added, removed: sprint.removed + unsaved.removed };
  const net = sprintNet(live);
  const clock = sprintClock(remaining(live, now));
  const words = live.goal !== null ? `${formatNumber(net)}/${formatNumber(live.goal)}` : `${net >= 0 ? '+' : '−'}${formatNumber(Math.abs(net))}`;
  const display = sessionDisplay();
  const shown = [display.timeLeft ? clock : null, display.words ? words : null].filter((s) => s !== null);
  const label = ['Session', display.timeLeft ? `${clock} left` : null, display.words ? formatWords(net) : null].filter((s) => s !== null).join(', ');
  return (
    <MenuButton
      label={label}
      className={`foot-button sprint-status${goalMet(live) ? ' met' : ''}`}
      title={display.words ? `Session: ${formatNumber(live.added)} written, ${formatNumber(live.removed)} cut` : 'A writing session is running'}
      entries={() => [
        { kind: 'heading', label: live.goal !== null ? `Goal: ${formatWords(live.goal)}` : 'No word goal' },
        { label: 'Finish Now', onSelect: () => void writing.finish() },
        { label: 'Discard Session', danger: true, onSelect: () => writing.discard() },
      ]}
    >
      {display.timeLeft ? (
        <span className="sprint-meter" aria-hidden="true">
          <span className="meter-fill" ref={(el) => el?.style.setProperty('--fraction', String(progress(live, now)))} />
        </span>
      ) : null}
      <span data-sprint-left={Math.ceil(remaining(live, now))} data-sprint-added={live.added} data-sprint-removed={live.removed}>
        {shown.length > 0 ? shown.join(' · ') : 'Session'}
      </span>
    </MenuButton>
  );
}

/** The pictures of the research notes shown (a clip's, a picture kept from Wikimedia; a study page's photos), above the
 *  text as the Mac's PhotoStrip shows them. Each opens full size in a tab. */
function PicturesStrip({ project, live, ids }: { project: PageProps['project']; live: LiveProject | null; ids: readonly string[] }) {
  const pictures = ids.flatMap((id) => (project.fragment(id)?.attachments ?? []).filter(isPicture));
  if (!pictures.length) return null;
  return (
    <div className="pictures-strip" aria-label="Pictures">
      {pictures.map((a) => <StripPicture key={a.sha256} live={live} attachment={a} />)}
    </div>
  );
}

function StripPicture({ live, attachment }: { live: LiveProject | null; attachment: AttachmentRef }) {
  const { url, missing } = useAttachmentURL(live, attachment);
  if (!url) return missing ? <span className="pictures-strip-missing field-note">Not on this device yet</span> : null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="pictures-strip-picture">
      <img src={url} alt={attachment.filename} />
    </a>
  );
}
